/**
 * "Did this request fail because the network is down, or because the server
 * said no?"
 *
 * The distinction is load-bearing for the session. A rejection with NO
 * response is the only thing that proves nothing was reached; a rejection
 * WITH a response is the server's answer, and only a 401 there means the
 * session is actually dead. Conflating the two is how a captive portal, a
 * DNS hiccup or one bad minute on the API used to sign every open app out
 * (see authStore.initializeAuth) — and, one level down, how a refresh that
 * failed for a transport reason used to make the interceptors throw away a
 * perfectly valid refresh token.
 *
 * The desktop build does not produce browser-native network errors: the Tauri
 * adapter (utils/tauriAxiosAdapter.js) rebuilds every non-axios rejection out
 * of the Rust HTTP command as
 * `AxiosError.from(new Error(msg), AxiosError.ERR_NETWORK, config, null, null)`
 * — an AxiosError with `code === 'ERR_NETWORK'` and, crucially, NO
 * `response`. Timeouts arrive as ECONNABORTED (axios' own timer) or
 * ETIMEDOUT (the OS'). The rest of the codes below are what reqwest/undici
 * surface for a dead resolver, a refused connection or a dropped socket; they
 * cost nothing to list and each one, misread as "session invalid", is an
 * unrecoverable logout.
 */

const NETWORK_ERROR_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ECONNREFUSED',
  'ECONNRESET',
  'ENOTFOUND',
  'EAI_AGAIN',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'ENETDOWN',
  'EPIPE',
]);

/** Codes that mean specifically "we waited and nothing came back". */
const TIMEOUT_ERROR_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

/**
 * True when the failure is a transport failure — the request never got an
 * answer.
 *
 * Deliberately CONSERVATIVE: it must never say "network" about a programming
 * error. A `TypeError` thrown inside a `.then()` has no `response` and no
 * `code` either, so a laxer "no response means offline" rule would classify
 * our own bugs as connectivity problems and hide them forever. A recognised
 * code (or axios' pre-1.x bare 'Network Error' message) is required.
 */
export function isNetworkError(error) {
  if (!error || typeof error !== 'object') return false;
  // The server answered. 5xx is a server fault and 401 is a dead session —
  // neither is a transport failure, and both must stay distinguishable here.
  if (error.response) return false;
  if (typeof error.code === 'string' && NETWORK_ERROR_CODES.has(error.code)) return true;
  // axios < 1 (and a few adapters) set only the message.
  return error.message === 'Network Error';
}

/** True for the subset of isNetworkError() that is specifically a timeout. */
export function isTimeoutError(error) {
  if (!error || typeof error !== 'object' || error.response) return false;
  return typeof error.code === 'string' && TIMEOUT_ERROR_CODES.has(error.code);
}

/**
 * True when the server explicitly rejected the credentials.
 *
 * This — and ONLY this — is evidence that a stored session is no longer
 * valid. Every token-clearing decision in the app should be gated on it
 * rather than on "the request failed".
 */
export function isAuthRejection(error) {
  return error?.response?.status === 401;
}
